import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X } from 'lucide-react';

const Header = ({ onBookClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';
  // Use the solid (light) header on every non-home route so the dark text/logo
  // stays readable over arbitrary hero images.
  const solid = isScrolled || !isHome;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    if (!isHome) {
      navigate(`/#${id}`);
      setIsMobileMenuOpen(false);
      return;
    }
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsMobileMenuOpen(false);
    }
  };

  const handleBook = () => {
    if (onBookClick) {
      onBookClick();
    } else {
      // when Header is used on a non-home page, route to homepage contact section
      navigate('/#contact');
    }
    setIsMobileMenuOpen(false);
  };

  const navLinks = [
    { id: 'about', label: 'Rólam' },
    { id: 'programs', label: 'Programok' },
    { id: 'miskolc', label: 'Miskolc', route: '/szemelyi-edzo-miskolc', desktopHiddenBelowXl: true },
    { id: 'online', label: 'Online coaching', route: '/online-szemelyi-edzo', desktopHiddenBelowXl: true },
    { id: 'calculator', label: 'Kalkulátor' },
    { id: 'lead-magnet', label: 'Ingyenes Útmutató' },
    { id: 'tudastar', label: 'Tudástár', route: '/tudastar' },
    { id: 'gyik', label: 'GYIK', route: '/gyik' },
    { id: 'contact', label: 'Kapcsolat' },
    { id: 'fiokom', label: 'Fiókom', route: '/fiokom' },
  ];

  const handleNavClick = (link) => {
    if (link.route) {
      navigate(link.route);
      setIsMobileMenuOpen(false);
      return;
    }
    scrollToSection(link.id);
  };

  return (
    <header 
      data-testid="site-header"
      className={`fixed left-0 right-0 z-50 transition-all duration-300 ${
        solid
          ? 'bg-white/95 backdrop-blur-md shadow-md'
          : 'bg-transparent'
      }`}
      style={{ top: 'var(--ann-h, 0px)' }}
    >
      <nav className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <div 
            data-testid="site-logo"
            className="flex items-center gap-2 md:gap-3 cursor-pointer flex-shrink-0"
            onClick={() => navigate('/')}
            aria-label="Orosz Coaching – főoldal"
          >
            <picture>
              <source srcSet="/orosz-gabor-logo.webp" type="image/webp" />
              <img
                src="/orosz-gabor-logo.png"
                alt="Orosz Coaching logó"
                className={`h-10 md:h-12 w-10 md:w-12 rounded-lg object-contain transition-shadow ${
                  solid ? 'shadow-sm' : 'shadow-md ring-1 ring-white/20'
                }`}
                loading="eager"
                decoding="async"
                width="512"
                height="512"
              />
            </picture>
            {/* Wordmark visible on mobile (<md, no nav links) AND on xl+ desktop
                (≥1280px, where the 6-link nav has room). Hidden on md–lg tablet
                widths where the nav would otherwise crash into it. */}
            <div
              aria-label="OROSZ COACHING"
              className="block md:hidden xl:block text-xl md:text-2xl font-bold tracking-tight leading-none"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              <span className={solid ? 'text-gray-900' : 'text-white'}>OROSZ</span>
              <span aria-hidden="true"> </span>
              <span className="text-yellow-600">COACHING</span>
            </div>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-5 lg:gap-8 ml-auto">
            {navLinks.map((link) => (
              <button 
                key={link.id}
                data-testid={`nav-${link.id}`}
                onClick={() => handleNavClick(link)}
                className={`${link.desktopHiddenBelowXl ? 'hidden xl:inline-block' : ''} text-sm lg:text-base font-medium whitespace-nowrap transition-colors ${
                  solid
                    ? 'text-gray-700 hover:text-yellow-600'
                    : 'text-white hover:text-yellow-400'
                }`}
              >
                {link.label}
              </button>
            ))}
            <Button 
              data-testid="header-cta-button"
              onClick={handleBook}
              className="bg-yellow-600 hover:bg-yellow-700 text-white font-semibold px-4 lg:px-6 text-sm lg:text-base whitespace-nowrap transition-all duration-300"
            >
              Időpontfoglalás
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden ${
              solid ? 'text-gray-900' : 'text-white'
            }`}
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div data-testid="mobile-menu" className="md:hidden mt-4 pb-4 space-y-4 bg-white/95 backdrop-blur-md rounded-lg p-4">
            {navLinks.map((link) => (
              <button 
                key={link.id}
                data-testid={`mobile-nav-${link.id}`}
                onClick={() => handleNavClick(link)}
                className="block w-full text-left py-2 text-gray-700 hover:text-yellow-600 font-medium"
              >
                {link.label}
              </button>
            ))}
            <Button 
              data-testid="mobile-cta-button"
              onClick={handleBook}
              className="w-full bg-yellow-600 hover:bg-yellow-700 text-white font-semibold"
            >
              Időpontfoglalás
            </Button>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
